"use client";

import "./CandidateProf.css";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Input,
  Select,
  Switch,
  Textarea,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stack,
  Text,
  FormLabel,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormHelperText,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ArrowRightIcon,
  SearchIcon,
  ArrowLeftIcon,
} from "@chakra-ui/icons";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  addExperienceAsync,
} from "../CandidateProfileSlice/Experience/ExperienceSlice2";
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice";
import {
  fetchPublicJobTitlesAsync,
  selectJobTitles,
} from "../../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice";
import { selectFetchedExperience } from "../CandidateProfileSlice/Experience/ExperienceSlice";

export default function Experience() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const minValidYear = 1970;

  // Generate years from current year down to 1970
  const years = Array.from({ length: currentYear - minValidYear + 1 }, (_, i) => currentYear - i);

  const monthList = [
    { value: 1, month: "January" },
    { value: 2, month: "February" },
    { value: 3, month: "March" },
    { value: 4, month: "April" },
    { value: 5, month: "May" },
    { value: 6, month: "June" },
    { value: 7, month: "July" },
    { value: 8, month: "August" },
    { value: 9, month: "September" },
    { value: 10, month: "October" },
    { value: 11, month: "November" },
    { value: 12, month: "December" },
  ];

  // Selectors
  const department = useSelector(selectJobDepartments);
  const jobTitle = useSelector(selectJobTitles);
  const jobTitlesLoading = useSelector((state) => state.jobTitles?.loading || false);
  const experience = useSelector(selectFetchedExperience);

  // State
  const [hasOffer, setHasOffer] = useState(false);
  const [isCurrentEmployment, setIsCurrentEmployment] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJobTitle, setSelectedJobTitle] = useState(null);
  const [offeredSearchTerm, setOfferedSearchTerm] = useState("");
  const [selectedOfferedJobTitle, setSelectedOfferedJobTitle] = useState(null);
  const [experienceMismatch, setExperienceMismatch] = useState(null);

  const isMountedRef = useRef(true);
  const cancelRef = useRef();

  // Disclosures for menus
  const {
    isOpen: isTitleMenuOpen,
    onOpen: onTitleMenuOpen,
    onClose: onTitleMenuClose,
  } = useDisclosure();

  const {
    isOpen: isOfferedTitleMenuOpen,
    onOpen: onOfferedTitleMenuOpen,
    onClose: onOfferedTitleMenuClose,
  } = useDisclosure();

  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();

  // Form setup
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      is_current_employement: false,
      experience_year: 0,
      experience_month: 0,
      start_year: currentYear,
      start_month: 1,
      end_year: currentYear,
      end_month: 1,
      current_salary_currency: "INR",
      offered_salary_currency: "INR",
      job_title_uuid: "",
      department_uuid: "",
      current_company: "",
      employement_type: "",
      current_salary_amount: "",
      current_pay_type: "",
      job_profile: "",
      notice_period: "",
      last_working_date: "",
      offered_job_title_uuid: "",
      offered_salary_amount: "",
      offered_pay_type: "",
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          dispatch(fetchJobDepartmentsAsync()),
          dispatch(fetchPublicJobTitlesAsync("")),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        if (isMountedRef.current) {
          toast({
            title: "Error loading data",
            description: "Failed to load form options. Please refresh the page.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        }
      }
    };

    fetchInitialData();
  }, [dispatch, toast]);

  // Check existing experience and redirect
  useEffect(() => {
    if (experience !== null) {
      setIsChecking(false);
      if (Array.isArray(experience) && experience.length > 0) {
        navigate("/candidate-dashboard", { replace: true });
      }
    }
  }, [experience, navigate]);

  // Debounced search for designation
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim() !== "" || isTitleMenuOpen) {
        dispatch(fetchPublicJobTitlesAsync(searchTerm.trim()));
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, isTitleMenuOpen, dispatch]);

  // Debounced search for offered designation
  useEffect(() => {
    const handler = setTimeout(() => {
      if (offeredSearchTerm.trim() !== "" || isOfferedTitleMenuOpen) {
        dispatch(fetchPublicJobTitlesAsync(offeredSearchTerm.trim()));
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [offeredSearchTerm, isOfferedTitleMenuOpen, dispatch]);

  // Validation functions
  const validateMonth = (value) => {
    if (!value) return "Month is required";
    const month = Number(value);
    return (month >= 1 && month <= 12) || "Month must be between 1 and 12";
  };

  const validateYear = (value) => {
    if (!value) return "Year is required";
    const year = Number(value);
    return (
      (year >= minValidYear && year <= currentYear) ||
      `Year must be between ${minValidYear} and ${currentYear}`
    );
  };

  const validateEndYear = (value) => {
    if (isCurrentEmployment) return true;
    if (!value) return "End year is required";

    const startYear = Number(getValues("start_year"));
    const endYear = Number(value);

    if (endYear < startYear) {
      return "End year cannot be before start year";
    }

    if (endYear > currentYear) {
      return `End year cannot be in the future (max: ${currentYear})`;
    }

    return (
      (endYear >= minValidYear && endYear <= currentYear) ||
      `Year must be between ${minValidYear} and ${currentYear}`
    );
  };

  const validateEndMonth = (value) => {
    if (isCurrentEmployment) return true;
    if (!value) return "End month is required";

    const month = Number(value);
    if (month < 1 || month > 12) {
      return "Month must be between 1 and 12";
    }

    const startYear = Number(getValues("start_year"));
    const endYear = Number(getValues("end_year"));
    const startMonth = Number(getValues("start_month"));

    if (startYear === endYear && month < startMonth) {
      return "End month must be after start month in the same year";
    }

    return true;
  };

  const validateSalary = (value) => {
    if (value === undefined || value === null || value === "")
      return "Salary is required";
    const num = Number(value);
    if (num < 0) return "Salary cannot be negative";
    if (num < 1000) return "Salary must be at least 1,000";
    if (num > 100000000) return "Salary seems unrealistic";
    return true;
  };

  const validateLastWorkingDate = (value) => {
    if (!hasOffer) return true;
    if (!value) return "Last working date is required";
    const selectedDate = new Date(value);
    const today = new Date();
    if (selectedDate > today) {
      return "Last working date cannot be in the future";
    }
    return true;
  };

  // Calculate experience based on dates
  const calculateExperience = (startYear, startMonth, endYear, endMonth) => {
    const sYear = Number(startYear);
    const sMonth = Number(startMonth);
    let eYear, eMonth;

    if (!endYear || !endMonth || isCurrentEmployment) {
      const currentDate = new Date();
      eYear = currentDate.getFullYear();
      eMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    } else {
      eYear = Number(endYear);
      eMonth = Number(endMonth);
    }

    let totalMonths = (eYear - sYear) * 12 + (eMonth - sMonth);
    totalMonths = Math.max(0, totalMonths);

    return {
      years: Math.floor(totalMonths / 12),
      months: totalMonths % 12,
    };
  };

  // Auto-calculate experience on date changes
  const updateExperience = () => {
    const startYear = getValues("start_year");
    const startMonth = getValues("start_month");
    const endYear = isCurrentEmployment ? null : getValues("end_year");
    const endMonth = isCurrentEmployment ? null : getValues("end_month");

    if (startYear && startMonth) {
      const { years, months } = calculateExperience(
        startYear,
        startMonth,
        endYear,
        endMonth
      );
      setValue("experience_year", years);
      setValue("experience_month", months);
    }
  };

  // Form submission
  const onSubmit = async (data) => {
    const { years: calculatedYears, months: calculatedMonths } =
      calculateExperience(
        data.start_year,
        data.start_month,
        isCurrentEmployment ? null : data.end_year,
        isCurrentEmployment ? null : data.end_month
      );

    // Check for experience mismatch
    if (
      Number(data.experience_year) !== calculatedYears ||
      Number(data.experience_month) !== calculatedMonths
    ) {
      setExperienceMismatch({
        calculated: { years: calculatedYears, months: calculatedMonths },
        current: { years: data.experience_year, months: data.experience_month },
        data: data,
      });
      onAlertOpen();
      return;
    }

    await submitExperience(data);
  };

  const submitExperience = async (data) => {
    const submissionData = { ...data };

    if (submissionData.is_current_employement) {
      submissionData.end_year = null;
      submissionData.end_month = null;
    }

    // Remove offer-related fields if hasOffer is false
    if (!hasOffer) {
      delete submissionData.last_working_date;
      delete submissionData.offered_job_title_uuid;
      delete submissionData.offered_salary_amount;
      delete submissionData.offered_salary_currency;
      delete submissionData.offered_pay_type;
    }

    try {
      const response = await dispatch(addExperienceAsync({ userData: submissionData }));

      if (!isMountedRef.current) return;

      if (response.payload && !response.error) {
        toast({
          title: "Experience saved",
          description: "Your experience details have been successfully saved",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });

        navigate("/candidate-profile/job-preferences");
      } else {
        toast({
          title: "Error saving experience",
          description: "Please check your form details and try again",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      console.error("Error saving experience:", error);
      toast({
        title: "Error saving experience",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleMismatchConfirm = () => {
    if (experienceMismatch) {
      const updatedData = {
        ...experienceMismatch.data,
        experience_year: experienceMismatch.calculated.years,
        experience_month: experienceMismatch.calculated.months,
      };
      setValue("experience_year", experienceMismatch.calculated.years);
      setValue("experience_month", experienceMismatch.calculated.months);
      submitExperience(updatedData);
    }
    onAlertClose();
    setExperienceMismatch(null);
  };

  const handlePrevious = () => {
    navigate("/candidate-profile/skills");
  };

  if (isChecking) {
    return (
      <Box
        minH="calc(100vh - 200px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack align="center" spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
          <Text fontSize="lg" color="gray.600">
            Loading experience form...
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box minH="calc(100vh - 200px)" >
      <Box maxW="7xl" mx="auto" bg="white" borderRadius="xl" shadow="sm" p={{ base: 4, md: 8 }}>
        <Text fontSize="2xl" fontWeight="semibold" mb={6}>
          Work Experience
        </Text>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={6}>
            {/* Current Employment Switch */}
            <FormControl>
              <Stack direction="row" alignItems="center">
                <FormLabel fontWeight="semibold" mb={0}>
                  Is this your current employment?
                </FormLabel>
                <Switch
                  colorScheme="teal"
                  size="lg"
                  isChecked={isCurrentEmployment}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setValue("is_current_employement", isChecked);
                    setIsCurrentEmployment(isChecked);
                    if (isChecked) {
                      setValue("end_year", "");
                      setValue("end_month", "");
                    }
                    updateExperience();
                  }}
                />
              </Stack>
              <FormHelperText>
                Toggle this if you are currently working in this position
              </FormHelperText>
            </FormControl>

            {/* Designation with Search Menu */}
            <FormControl isInvalid={!!errors.job_title_uuid} isRequired>
              <FormLabel fontWeight="semibold">Designation</FormLabel>
              <Input
                type="hidden"
                {...register("job_title_uuid", {
                  required: "Designation is required",
                })}
              />
              <Menu
                isOpen={isTitleMenuOpen}
                onClose={() => {
                  onTitleMenuClose();
                  setSearchTerm("");
                }}
                closeOnSelect={false}
                isLazy
              >
                <MenuButton
                  as={Button}
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  width="100%"
                  justifyContent="space-between"
                  onClick={isTitleMenuOpen ? onTitleMenuClose : onTitleMenuOpen}
                  textAlign="left"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.job_title_uuid ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.job_title_uuid ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                  autoFocus
                >
                  {selectedJobTitle?.title || "Select Designation"}
                </MenuButton>
                <MenuList p={2} minW="100%" maxH="320px" overflowY="auto" zIndex={10}>
                  <InputGroup mb={2}>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.500" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search designations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </InputGroup>
                  {jobTitlesLoading ? (
                    <Stack align="center" justify="center" py={4}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Loading...</Text>
                    </Stack>
                  ) : Array.isArray(jobTitle) && jobTitle.length > 0 ? (
                    jobTitle.map((item) => (
                      <MenuItem
                        key={item.uuid}
                        onClick={() => {
                          setSelectedJobTitle({
                            uuid: item.uuid,
                            title: item.title,
                          });
                          setValue("job_title_uuid", item.uuid, {
                            shouldValidate: true,
                          });
                          onTitleMenuClose();
                          setSearchTerm("");
                        }}
                      >
                        {item.title}
                      </MenuItem>
                    ))
                  ) : (
                    <Box px={3} py={2} color="gray.500" fontSize="sm">
                      No results found
                    </Box>
                  )}
                </MenuList>
              </Menu>
              <FormErrorMessage>
                {errors.job_title_uuid?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Department */}
            <FormControl isInvalid={!!errors.department_uuid} isRequired>
              <FormLabel fontWeight="semibold">Department</FormLabel>
              <Select
                placeholder="Select Department"
                {...register("department_uuid", {
                  required: "Department is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.department_uuid ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.department_uuid ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                {Array.isArray(department) &&
                  department.map((dept) => (
                    <option key={dept.uuid} value={dept.uuid}>
                      {dept.title}
                    </option>
                  ))}
              </Select>
              <FormErrorMessage>
                {errors.department_uuid?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Company Name */}
            <FormControl isInvalid={!!errors.current_company} isRequired>
              <FormLabel fontWeight="semibold">Company Name</FormLabel>
              <Input
                placeholder="Enter company name"
                {...register("current_company", {
                  required: "Company name is required",
                  minLength: {
                    value: 2,
                    message: "Company name must be at least 2 characters",
                  },
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.current_company ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.current_company ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              />
              <FormErrorMessage>
                {errors.current_company?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Employment Type */}
            <FormControl isInvalid={!!errors.employement_type} isRequired>
              <FormLabel fontWeight="semibold">Employment Type</FormLabel>
              <Select
                placeholder="Select Employment Type"
                {...register("employement_type", {
                  required: "Employment type is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.employement_type ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.employement_type ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Contract">Contract</option>
              </Select>
              <FormErrorMessage>
                {errors.employement_type?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Start Date */}
            <Stack direction={{ base: "column", md: "row" }} spacing={4}>
              <FormControl isInvalid={!!errors.start_year} isRequired flex="1">
                <FormLabel fontWeight="semibold">Start Year</FormLabel>
                <Select
                  placeholder="Select Year"
                  {...register("start_year", {
                    required: "Start year is required",
                    validate: validateYear,
                  })}
                  onChange={(e) => {
                    setValue("start_year", e.target.value);
                    updateExperience();
                    if (!isCurrentEmployment) {
                      trigger(["end_year", "end_month"]);
                    }
                  }}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.start_year ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.start_year ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.start_year?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.start_month} isRequired flex="1">
                <FormLabel fontWeight="semibold">Start Month</FormLabel>
                <Select
                  placeholder="Select Month"
                  {...register("start_month", {
                    required: "Start month is required",
                    validate: validateMonth,
                  })}
                  onChange={(e) => {
                    setValue("start_month", e.target.value);
                    updateExperience();
                    if (!isCurrentEmployment) {
                      trigger("end_month");
                    }
                  }}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.start_month ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.start_month ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  {monthList.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.month}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>
                  {errors.start_month?.message}
                </FormErrorMessage>
              </FormControl>
            </Stack>

            {/* End Date (conditional) */}
            {!isCurrentEmployment && (
              <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                <FormControl isInvalid={!!errors.end_year} isRequired flex="1">
                  <FormLabel fontWeight="semibold">End Year</FormLabel>
                  <Select
                    placeholder="Select Year"
                    {...register("end_year", {
                      required: !isCurrentEmployment && "End year is required",
                      validate: validateEndYear,
                    })}
                    onChange={(e) => {
                      setValue("end_year", e.target.value);
                      updateExperience();
                      trigger("end_month");
                    }}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={errors.end_year ? "red.500" : "gray.200"}
                    _focus={{
                      borderColor: errors.end_year ? "red.500" : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.end_year?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.end_month} isRequired flex="1">
                  <FormLabel fontWeight="semibold">End Month</FormLabel>
                  <Select
                    placeholder="Select Month"
                    {...register("end_month", {
                      required: !isCurrentEmployment && "End month is required",
                      validate: validateEndMonth,
                    })}
                    onChange={(e) => {
                      setValue("end_month", e.target.value);
                      updateExperience();
                    }}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={errors.end_month ? "red.500" : "gray.200"}
                    _focus={{
                      borderColor: errors.end_month ? "red.500" : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  >
                    {monthList.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.month}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.end_month?.message}</FormErrorMessage>
                </FormControl>
              </Stack>
            )}

            {/* Calculated Experience (readonly) */}
            <Stack direction={{ base: "column", md: "row" }} spacing={4}>
              <FormControl flex="1">
                <FormLabel fontWeight="semibold">Experience (Years)</FormLabel>
                <Input
                  type="number"
                  {...register("experience_year")}
                  placeholder="Auto-calculated"
                  bg="gray.50"
                  isReadOnly
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="gray.200"
                />
                <FormHelperText>
                  Automatically calculated from dates
                </FormHelperText>
              </FormControl>

              <FormControl flex="1">
                <FormLabel fontWeight="semibold">Experience (Months)</FormLabel>
                <Input
                  type="number"
                  {...register("experience_month")}
                  placeholder="Auto-calculated"
                  bg="gray.50"
                  isReadOnly
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="gray.200"
                />
                <FormHelperText>
                  Automatically calculated from dates
                </FormHelperText>
              </FormControl>
            </Stack>

            {/* Salary Information */}
            <Stack direction={{ base: "column", md: "row" }} spacing={4}>
              <FormControl
                isInvalid={!!errors.current_salary_currency}
                isRequired
                flex="1"
              >
                <FormLabel fontWeight="semibold">Currency</FormLabel>
                <Select
                  {...register("current_salary_currency", {
                    required: "Currency is required",
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={
                    errors.current_salary_currency ? "red.500" : "gray.200"
                  }
                  _focus={{
                    borderColor: errors.current_salary_currency
                      ? "red.500"
                      : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </Select>
                <FormErrorMessage>
                  {errors.current_salary_currency?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!errors.current_salary_amount}
                isRequired
                flex="2"
              >
                <FormLabel fontWeight="semibold">Current CTC</FormLabel>
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  {...register("current_salary_amount", {
                    required: "Salary amount is required",
                    validate: validateSalary,
                  })}
                  placeholder="Enter amount"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={
                    errors.current_salary_amount ? "red.500" : "gray.200"
                  }
                  _focus={{
                    borderColor: errors.current_salary_amount
                      ? "red.500"
                      : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                />
                <FormErrorMessage>
                  {errors.current_salary_amount?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.current_pay_type} isRequired flex="1">
                <FormLabel fontWeight="semibold">Pay Type</FormLabel>
                <Select
                  {...register("current_pay_type", {
                    required: "Pay Type is required",
                  })}
                  placeholder="Select Pay Type"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.current_pay_type ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.current_pay_type ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  <option value="Per Month">Per Month</option>
                  <option value="Per Year">Per Year</option>
                </Select>
                <FormErrorMessage>
                  {errors.current_pay_type?.message}
                </FormErrorMessage>
              </FormControl>
            </Stack>

            {/* Job Profile/KRA */}
            <FormControl isInvalid={!!errors.job_profile} isRequired>
              <FormLabel fontWeight="semibold">Key Responsibility Area</FormLabel>
              <Textarea
                {...register("job_profile", {
                  required: "Responsibility area is required",
                  maxLength: {
                    value: 1000,
                    message: "Maximum 1000 characters allowed",
                  },
                })}
                placeholder="Describe your key responsibilities..."
                rows={4}
                maxLength={1000}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.job_profile ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.job_profile ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              />
              <FormHelperText>
                {watch("job_profile")?.length || 0}/1000 characters
              </FormHelperText>
              <FormErrorMessage>{errors.job_profile?.message}</FormErrorMessage>
            </FormControl>

            {/* Notice Period */}
            <FormControl isInvalid={!!errors.notice_period} isRequired>
              <FormLabel fontWeight="semibold">Notice Period</FormLabel>
              <Select
                placeholder="Select Notice Period"
                {...register("notice_period", {
                  required: "Notice Period is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.notice_period ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.notice_period ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Immediate Join">Immediate Join</option>
                <option value="15 Days or less">15 Days or less</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
                <option value="More than 3 months">More than 3 months</option>
                <option value="Serving Notice Period">
                  Serving Notice Period
                </option>
              </Select>
              <FormErrorMessage>{errors.notice_period?.message}</FormErrorMessage>
            </FormControl>

            {/* Has Offer Checkbox */}
            <FormControl>
              <Checkbox
                isChecked={hasOffer}
                onChange={(e) => setHasOffer(e.target.checked)}
                colorScheme="blue"
              >
                Do you have any offer from another company?
              </Checkbox>
            </FormControl>

            {/* Conditional Offer Fields */}
            {hasOffer && (
              <Box
                p={4}
                bg="blue.50"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <Stack spacing={4}>
                  {/* Last Working Date */}
                  <FormControl isInvalid={!!errors.last_working_date} isRequired>
                    <FormLabel fontWeight="semibold">Last Working Date</FormLabel>
                    <Input
                      type="date"
                      {...register("last_working_date", {
                        required: hasOffer && "Last working date is required",
                        validate: validateLastWorkingDate,
                      })}
                      max={new Date().toISOString().split("T")[0]}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg="white"
                      borderColor={
                        errors.last_working_date ? "red.500" : "gray.200"
                      }
                      _focus={{
                        borderColor: errors.last_working_date
                          ? "red.500"
                          : "blue.500",
                        boxShadow: "0 0 0 1px",
                      }}
                    />
                    <FormErrorMessage>
                      {errors.last_working_date?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Offered Designation */}
                  <FormControl
                    isInvalid={!!errors.offered_job_title_uuid}
                    isRequired
                  >
                    <FormLabel fontWeight="semibold">Offered Designation</FormLabel>
                    <Input
                      type="hidden"
                      {...register("offered_job_title_uuid", {
                        required: hasOffer && "Offered designation is required",
                      })}
                    />
                    <Menu
                      isOpen={isOfferedTitleMenuOpen}
                      onClose={() => {
                        onOfferedTitleMenuClose();
                        setOfferedSearchTerm("");
                      }}
                      closeOnSelect={false}
                      isLazy
                    >
                      <MenuButton
                        as={Button}
                        variant="outline"
                        rightIcon={<ChevronDownIcon />}
                        width="100%"
                        justifyContent="space-between"
                        onClick={
                          isOfferedTitleMenuOpen
                            ? onOfferedTitleMenuClose
                            : onOfferedTitleMenuOpen
                        }
                        textAlign="left"
                        bg="white"
                        borderWidth="1px"
                        borderRadius="lg"
                        borderColor={
                          errors.offered_job_title_uuid ? "red.500" : "gray.200"
                        }
                        _focus={{
                          borderColor: errors.offered_job_title_uuid
                            ? "red.500"
                            : "blue.500",
                          boxShadow: "0 0 0 1px",
                        }}
                      >
                        {selectedOfferedJobTitle?.title ||
                          "Select Offered Designation"}
                      </MenuButton>
                      <MenuList
                        p={2}
                        minW="100%"
                        maxH="320px"
                        overflowY="auto"
                        zIndex={10}
                      >
                        <InputGroup mb={2}>
                          <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.500" />
                          </InputLeftElement>
                          <Input
                            placeholder="Search designations..."
                            value={offeredSearchTerm}
                            onChange={(e) => setOfferedSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </InputGroup>
                        {jobTitlesLoading ? (
                          <Stack align="center" justify="center" py={4}>
                            <Spinner size="sm" />
                            <Text fontSize="sm">Loading...</Text>
                          </Stack>
                        ) : Array.isArray(jobTitle) && jobTitle.length > 0 ? (
                          jobTitle.map((item) => (
                            <MenuItem
                              key={item.uuid}
                              onClick={() => {
                                setSelectedOfferedJobTitle({
                                  uuid: item.uuid,
                                  title: item.title,
                                });
                                setValue("offered_job_title_uuid", item.uuid, {
                                  shouldValidate: true,
                                });
                                onOfferedTitleMenuClose();
                                setOfferedSearchTerm("");
                              }}
                            >
                              {item.title}
                            </MenuItem>
                          ))
                        ) : (
                          <Box px={3} py={2} color="gray.500" fontSize="sm">
                            No results found
                          </Box>
                        )}
                      </MenuList>
                    </Menu>
                    <FormErrorMessage>
                      {errors.offered_job_title_uuid?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Offered Salary */}
                  <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                    <FormControl
                      isInvalid={!!errors.offered_salary_currency}
                      isRequired
                      flex="1"
                    >
                      <FormLabel fontWeight="semibold">Currency</FormLabel>
                      <Select
                        {...register("offered_salary_currency", {
                          required: hasOffer && "Currency is required",
                        })}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="lg"
                        borderColor={
                          errors.offered_salary_currency ? "red.500" : "gray.200"
                        }
                        _focus={{
                          borderColor: errors.offered_salary_currency
                            ? "red.500"
                            : "blue.500",
                          boxShadow: "0 0 0 1px",
                        }}
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </Select>
                      <FormErrorMessage>
                        {errors.offered_salary_currency?.message}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={!!errors.offered_salary_amount}
                      isRequired
                      flex="2"
                    >
                      <FormLabel fontWeight="semibold">Offered CTC</FormLabel>
                      <Input
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        {...register("offered_salary_amount", {
                          required: hasOffer && "Offered salary amount is required",
                          validate: hasOffer && validateSalary,
                        })}
                        placeholder="Enter amount"
                        bg="white"
                        borderWidth="1px"
                        borderRadius="lg"
                        borderColor={
                          errors.offered_salary_amount ? "red.500" : "gray.200"
                        }
                        _focus={{
                          borderColor: errors.offered_salary_amount
                            ? "red.500"
                            : "blue.500",
                          boxShadow: "0 0 0 1px",
                        }}
                      />
                      <FormErrorMessage>
                        {errors.offered_salary_amount?.message}
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      isInvalid={!!errors.offered_pay_type}
                      isRequired
                      flex="1"
                    >
                      <FormLabel fontWeight="semibold">Pay Type</FormLabel>
                      <Select
                        {...register("offered_pay_type", {
                          required: hasOffer && "Pay Type is required",
                        })}
                        placeholder="Select Pay Type"
                        bg="white"
                        borderWidth="1px"
                        borderRadius="lg"
                        borderColor={
                          errors.offered_pay_type ? "red.500" : "gray.200"
                        }
                        _focus={{
                          borderColor: errors.offered_pay_type
                            ? "red.500"
                            : "blue.500",
                          boxShadow: "0 0 0 1px",
                        }}
                      >
                        <option value="Per Month">Per Month</option>
                        <option value="Per Year">Per Year</option>
                      </Select>
                      <FormErrorMessage>
                        {errors.offered_pay_type?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Please fix the following errors:</Text>
                  <Text fontSize="sm" mt={1}>
                    {Object.keys(errors).length} field(s) require attention
                  </Text>
                </Box>
              </Alert>
            )}

            {/* Navigation Buttons */}
            <Box textAlign="center" mt={8}>
              <Stack
                direction={{ base: "column", sm: "row" }}
                spacing={4}
                justifyContent="center"
              >
                <Button
                  leftIcon={<ArrowLeftIcon />}
                  colorScheme="gray"
                  onClick={handlePrevious}
                  width={{ base: "full", sm: "auto" }}
                  minW="150px"
                  isDisabled={isSubmitting}
                >
                  Previous
                </Button>
                <Button
                  rightIcon={<ArrowRightIcon />}
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Saving..."
                  width={{ base: "full", sm: "auto" }}
                  minW="150px"
                >
                  Next
                </Button>
              </Stack>
            </Box>
          </Stack>
        </form>

        {/* Experience Mismatch Alert Dialog */}
        <AlertDialog
          isOpen={isAlertOpen}
          leastDestructiveRef={cancelRef}
          onClose={onAlertClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Experience Mismatch Detected
              </AlertDialogHeader>

              <AlertDialogBody>
                <Text mb={3}>
                  Based on your start and end dates, your experience should be:
                </Text>
                <Box bg="blue.50" p={3} borderRadius="md" mb={3}>
                  <Text fontWeight="bold" color="blue.700">
                    {experienceMismatch?.calculated.years} years and{" "}
                    {experienceMismatch?.calculated.months} months
                  </Text>
                </Box>
                <Text fontSize="sm" color="gray.600">
                  We've automatically updated the experience fields. Click
                  "Continue" to save with the corrected values, or "Cancel" to
                  review your dates.
                </Text>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAlertClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={handleMismatchConfirm} ml={3}>
                  Continue
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </Box>
  );
}
